import { EBOOK_BUNDLE_SENTINEL } from '@/ebookBundle'

export type ProductDisplayOption = {
  id: number
  description: string
  price: number
  salePrice?: number | null
  priceIncVat?: number | null
  salePriceIncVat?: number | null
  taxType?: number | null
  currencySymbol: string
  currencyCode?: string | null
  inStock: boolean
  productType: string
  needsShipping: boolean
  viPoints: number
}

type ProductOptionInput = {
  id?: number | null
  description?: string | null
  price: number
  salePrice?: number | null
  priceIncVat?: number | null
  salePriceIncVat?: number | null
  taxType?: number | null
  currency?: { symbol?: string | null; code?: string | null } | null
  inStock: boolean
  productType?: { name?: string | null } | null
  needsShipping?: boolean
  viPoints?: number
}

type EbookBundleOptionInput = {
  physicalOptionId: number
  downloadOptionId: number
  price: number
  currency?: { symbol?: string | null; code?: string | null } | null
} | null

/** Maps product options to display format, appending ebook bundle option when eligible. */
export function toProductDisplayOptions(
  productOptions: ProductOptionInput[] | undefined,
  ebookBundleOption: EbookBundleOptionInput
): ProductDisplayOption[] {
  const base = (productOptions ?? []).map(opt => ({
    id: opt.id ?? 0,
    description: opt.description ?? '',
    price: opt.price,
    salePrice: opt.salePrice ?? null,
    priceIncVat: opt.priceIncVat,
    salePriceIncVat: opt.salePriceIncVat,
    taxType: opt.taxType,
    currencySymbol: opt.currency?.symbol ?? '',
    currencyCode: opt.currency?.code,
    inStock: opt.inStock,
    productType: opt.productType?.name ?? '',
    needsShipping: opt.needsShipping ?? false,
    viPoints: opt.viPoints ?? 0,
  }))

  if (!ebookBundleOption) {
    return base
  }

  // VI points are earned on the price paid. Bundle has a discounted price, so
  // calculate points from the bundle price (10 per dollar, ceiling), matching
  // the API's PointsService.CalculatePointsForProduct formula.
  const POINTS_PER_DOLLAR = 10
  const bundleViPoints =
    Math.ceil(ebookBundleOption.price) * POINTS_PER_DOLLAR

  const bundleOption: ProductDisplayOption = {
    id: EBOOK_BUNDLE_SENTINEL,
    description: 'Book and ebook bundle',
    price: ebookBundleOption.price,
    salePrice: null,
    priceIncVat: null,
    salePriceIncVat: null,
    taxType: null,
    currencySymbol: ebookBundleOption.currency?.symbol ?? '',
    currencyCode: ebookBundleOption.currency?.code,
    inStock: true,
    productType: 'Bundle',
    needsShipping: true,
    viPoints: bundleViPoints,
  }

  return [...base, bundleOption]
}
